import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';

export const InteractionIntent = {
  OpenEditLegalClientDialog: 'openEditLegalClientDialog',
  CloseEditLegalClientDialog: 'closeEditLegalClientDialog',
} as const;

export type InteractionIntent =
  (typeof InteractionIntent)[keyof typeof InteractionIntent];

export type InteractionListener = (
  intent: InteractionIntent,
  payload?: any
) => void;

export const FamilyTreeInteractionContext = createContext<{
  listeners: Array<InteractionListener>;
  register: (listener: InteractionListener) => void;
  unregister: (listener: InteractionListener) => void;
  openEditLegalClientDialog: (legalClientId: number) => void;
  closeEditLegalClientDialog: () => void;
}>({
  listeners: [],
  register: listener => {},
  unregister: listener => {},
  openEditLegalClientDialog: () => {},
  closeEditLegalClientDialog: () => {},
});

export function FamilyTreeInteractionProvider({ children }: PropsWithChildren) {
  const [listeners, setListeners] = useState<InteractionListener[]>([]);

  const register = useCallback(
    (listener: InteractionListener) => {
      setListeners(prev => [...prev, listener]);
    },
    [setListeners]
  );

  const unregister = useCallback(
    (listener: InteractionListener) => {
      setListeners(prev => prev.filter(l => l !== listener));
    },
    [setListeners]
  );

  const openEditLegalClientDialog = useCallback(
    (legalClientId: number) => {
      listeners.forEach(listener =>
        listener(InteractionIntent.OpenEditLegalClientDialog, { legalClientId })
      );
    },
    [listeners]
  );

  const closeEditLegalClientDialog = useCallback(() => {
    listeners.forEach(listener =>
      listener(InteractionIntent.CloseEditLegalClientDialog)
    );
  }, [listeners]);

  return (
    <FamilyTreeInteractionContext.Provider
      value={{
        listeners,
        register,
        unregister,
        openEditLegalClientDialog,
        closeEditLegalClientDialog,
      }}
    >
      {children}
    </FamilyTreeInteractionContext.Provider>
  );
}

export function useFamilyTreeInteractionContext() {
  const context = useContext(FamilyTreeInteractionContext);
  if (!context) {
    throw new Error(
      'useFamilyTreeInteraction must be used within a FamilyTreeInteractionProvider'
    );
  }
  return context;
}

export function withFamilyTreeInteractionContext<P extends object>(
  Component: React.ComponentType<P>
) {
  const Wrapped: React.FC<P> = props => (
    <FamilyTreeInteractionProvider>
      <Component {...props} />
    </FamilyTreeInteractionProvider>
  );

  Wrapped.displayName = `withFamilyTreeInteractionContext(${
    Component.displayName ?? Component.name ?? 'Component'
  })`;
  return Wrapped;
}
